<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MetaBusinessAgentAPI from 'dashboard/api/metaBusinessAgent';

import Button from 'dashboard/components-next/button/Button.vue';
import SwitchNext from 'dashboard/components-next/switch/Switch.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  hookId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const T = key => t(`INTEGRATION_SETTINGS.META_BUSINESS_AGENT.SETTINGS.${key}`);

const isSaving = ref(false);
const rolloutEnabled = ref(false);
const audience = ref('EVERYONE');
const neverSayPhrasesText = ref('');

const handoff = reactive({
  enabled: true,
  message: '',
  message_selection: 'DEFAULT',
});

const followup = reactive({
  enabled: false,
  followup_interval_in_seconds: 0,
  message: '',
});

const FOLLOWUP_INTERVALS = [0, 300, 900, 1800, 3600, 7200, 28800, 86400];

const fetchSettings = async () => {
  try {
    const { data } = await MetaBusinessAgentAPI.getSettings(props.hookId);
    const settings = Array.isArray(data) ? data[0] : data;
    if (!settings) return;
    rolloutEnabled.value = !!settings.rollout?.enabled;
    audience.value = settings.ai_audience || 'EVERYONE';
    Object.assign(handoff, settings.handoff || {});
    Object.assign(followup, settings.followup || {});
    neverSayPhrasesText.value = (settings.never_say_phrases || []).join('\n');
  } catch (error) {
    useAlert(T('API.FETCH_ERROR'));
  }
};

onMounted(fetchSettings);

const neverSayPhrases = computed(() =>
  neverSayPhrasesText.value
    .split('\n')
    .map(phrase => phrase.trim())
    .filter(Boolean)
);

const save = async () => {
  isSaving.value = true;
  try {
    await MetaBusinessAgentAPI.updateSettings(props.hookId, {
      rollout: { enabled: rolloutEnabled.value },
      ai_audience: audience.value,
      handoff,
      followup,
      never_say_phrases: neverSayPhrases.value,
    });
    useAlert(T('API.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(T('API.UPDATE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-6 max-w-2xl">
    <div class="flex items-center gap-3">
      <SwitchNext v-model="rolloutEnabled" @change="save" />
      <span class="text-sm text-n-slate-12">{{ T('ROLLOUT_LABEL') }}</span>
    </div>

    <div>
      <label class="text-sm text-n-slate-11">{{ T('AUDIENCE_LABEL') }}</label>
      <select v-model="audience">
        <option value="EVERYONE">{{ T('AUDIENCE_EVERYONE') }}</option>
        <option value="ALLOWLISTED_ONLY">
          {{ T('AUDIENCE_ALLOWLISTED') }}
        </option>
      </select>
    </div>

    <div class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-1">
      <h4 class="text-sm font-medium text-n-slate-12 m-0">
        {{ T('HANDOFF_TITLE') }}
      </h4>
      <div class="flex items-center gap-3">
        <SwitchNext v-model="handoff.enabled" />
        <span class="text-sm text-n-slate-12">{{
          T('HANDOFF_ENABLED_LABEL')
        }}</span>
      </div>
      <div>
        <label class="text-sm text-n-slate-11">{{
          T('HANDOFF_MESSAGE_SELECTION_LABEL')
        }}</label>
        <select v-model="handoff.message_selection">
          <option value="DEFAULT">
            {{ T('HANDOFF_MESSAGE_SELECTION_DEFAULT') }}
          </option>
          <option value="AGENT">
            {{ T('HANDOFF_MESSAGE_SELECTION_AGENT') }}
          </option>
          <option value="CUSTOM">
            {{ T('HANDOFF_MESSAGE_SELECTION_CUSTOM') }}
          </option>
        </select>
      </div>
      <Input
        v-if="handoff.message_selection === 'CUSTOM'"
        v-model="handoff.message"
        :label="T('HANDOFF_MESSAGE_LABEL')"
      />
    </div>

    <div class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-1">
      <h4 class="text-sm font-medium text-n-slate-12 m-0">
        {{ T('FOLLOWUP_TITLE') }}
      </h4>
      <div class="flex items-center gap-3">
        <SwitchNext v-model="followup.enabled" />
        <span class="text-sm text-n-slate-12">{{
          T('FOLLOWUP_ENABLED_LABEL')
        }}</span>
      </div>
      <div v-if="followup.enabled">
        <label class="text-sm text-n-slate-11">{{
          T('FOLLOWUP_INTERVAL_LABEL')
        }}</label>
        <select v-model.number="followup.followup_interval_in_seconds">
          <option
            v-for="seconds in FOLLOWUP_INTERVALS"
            :key="seconds"
            :value="seconds"
          >
            {{ seconds }}
          </option>
        </select>
        <Input
          v-model="followup.message"
          :label="T('FOLLOWUP_MESSAGE_LABEL')"
        />
      </div>
    </div>

    <TextArea
      v-model="neverSayPhrasesText"
      :label="T('NEVER_SAY_PHRASES_LABEL')"
      :max-length="4000"
    />

    <div class="flex justify-end">
      <Button :label="T('SAVE_BUTTON')" :is-loading="isSaving" @click="save" />
    </div>
  </div>
</template>
